import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, CheckCircle, Clock,
  Star, Zap, MessageCircle, Phone, Gift, Crown,
  Bell, Shield, TrendingUp
} from 'lucide-react';
import apiClient from '@/utils/api';
import ListingFormEmbed from '@/components/ListingFormEmbed';

const SellIndividual = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [addonPlans, setAddonPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('12m');

  useEffect(() => {
    apiClient.get('/marketplace/seller-plans').then(r => setPlans(r.data?.plans || [])).catch(() => {});
    apiClient.get('/marketplace/addon-plans').then(r => setAddonPlans(r.data?.plans || [])).catch(() => {});
  }, []);

  const plan6m = plans.find(p => p.plan_name === 'seller_basic_6m') || { price: 990, listing_validity_days: 30, max_active_listings: 3, duration_days: 180 };
  const plan12m = plans.find(p => p.plan_name === 'seller_basic_12m') || { price: 1980, listing_validity_days: 30, max_active_listings: 3, duration_days: 365 };
  const selectedPaid = billingCycle === '6m' ? plan6m : plan12m;

  return (
    <div className="min-h-screen bg-white" data-testid="sell-individual-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-950 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <Gift className="w-4 h-4" /> Start Free — No Credit Card Required
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Sell Your Property<br />
              <span className="text-emerald-400">Without Brokerage</span>
            </h1>
            <p className="text-base text-slate-300 max-w-xl">
              List directly to thousands of verified buyers. Start free or upgrade for extended visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Main Split Layout */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-12 gap-8 items-start">

          {/* LEFT: Plans & Benefits */}
          <div className="lg:col-span-5 space-y-10 order-2 lg:order-1">

            {/* How It Works */}
            <div>
              <h2 className="text-xl font-bold mb-6">Sell in 3 Simple Steps</h2>
              <div className="space-y-4">
                {[
                  { n: 1, title: 'Add Property Details', desc: 'Fill in location, type, price and key features' },
                  { n: 2, title: 'Upload Photos', desc: 'Add high-quality images to attract genuine buyers' },
                  { n: 3, title: 'Go Live Instantly', desc: 'Your listing reaches thousands of buyers' },
                ].map(s => (
                  <div key={s.n} className="flex items-start gap-4">
                    <div className="w-9 h-9 bg-emerald-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-md shadow-emerald-500/20">{s.n}</div>
                    <div>
                      <p className="font-semibold text-sm">{s.title}</p>
                      <p className="text-xs text-slate-500">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Plans */}
            <div id="pricing">
              <h2 className="text-xl font-bold mb-2">Choose Your Selling Plan</h2>
              <p className="text-sm text-slate-500 mb-6">Start free or upgrade for more listings and longer visibility</p>

              {/* Free Plan */}
              <div className="border-2 border-slate-200 rounded-2xl p-5 mb-4" data-testid="plan-free">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-[10px] font-medium"><Gift className="w-3 h-3" /> Free Forever</span>
                    <h3 className="text-lg font-bold mt-1">Free Listing</h3>
                  </div>
                  <span className="text-3xl font-bold">₹0</span>
                </div>
                <ul className="space-y-2 mb-4">
                  {['1 Active Listing', '15-Day Visibility', 'Photo Upload (up to 10)', 'Dashboard Access'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Seller Basic */}
              <div className="border-2 border-emerald-500 rounded-2xl p-5 mb-4 relative shadow-lg shadow-emerald-500/10" data-testid="plan-seller-basic">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">MOST POPULAR</div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-medium"><Crown className="w-3 h-3" /> Seller Membership</span>
                    <h3 className="text-lg font-bold mt-1">Seller Basic</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold">₹{selectedPaid.price?.toLocaleString()}</span>
                    <span className="text-slate-500 text-xs">/{billingCycle === '6m' ? '6 mo' : 'year'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 my-3 bg-slate-100 rounded-lg p-1 w-fit">
                  <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${billingCycle === '6m' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} onClick={() => setBillingCycle('6m')}>6 Months</button>
                  <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${billingCycle === '12m' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} onClick={() => setBillingCycle('12m')}>12 Months</button>
                </div>
                <ul className="space-y-2 mb-2">
                  {['3 Active Listings (Sell + Rent)', '30-Day Validity per Listing', 'Unlimited Extensions', 'Priority in Search', 'WhatsApp Lead Alerts'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pay-Per-Extension */}
              <div className="border-2 border-slate-200 rounded-2xl p-5" data-testid="plan-pay-per-extend">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium"><Clock className="w-3 h-3" /> On Demand</span>
                    <h3 className="text-lg font-bold mt-1">Listing Extension</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold">₹199</span>
                    <span className="text-slate-500 text-xs">/ext</span>
                  </div>
                </div>
                <ul className="space-y-2">
                  {['Extend by 30 Days', 'Pay Only When Needed', 'Works with Free Plan', 'Instant Activation'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* WhatsApp Add-On */}
            <div id="addons">
              <h2 className="text-xl font-bold mb-2">WhatsApp Alert Add-On</h2>
              <p className="text-sm text-slate-500 mb-4">Never miss a buyer inquiry</p>
              <div className="space-y-3">
                {addonPlans.map(addon => (
                  <div key={addon.addon_name} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`addon-${addon.addon_name}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center"><MessageCircle className="w-4 h-4 text-white" /></div>
                        <h3 className="font-semibold text-sm">{addon.duration_days === 180 ? '6-Month' : '12-Month'} Plan</h3>
                      </div>
                      <span className="text-xl font-bold">₹{addon.price}</span>
                    </div>
                    <ul className="space-y-1">
                      {(addon.features || []).slice(0, 3).map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600"><Bell className="w-3 h-3 text-green-500" /> {f}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Boost Promo */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
              <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-bold mb-3"><Zap className="w-3 h-3" /> BOOST LISTING</div>
              <h3 className="font-bold mb-1">Get 5x More Visibility</h3>
              <p className="text-sm text-slate-600 mb-3">Boosted listings appear at the top of search results.</p>
              <ul className="space-y-1.5">
                {['Top of search results', 'Highlighted badge', 'Up to 5x more views'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-700"><Star className="w-3.5 h-3.5 text-amber-500" /> {f}</li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-3">Starting from <span className="font-bold text-slate-900">₹499</span> for 7 days</p>
            </div>

            {/* Trust */}
            <div className="grid grid-cols-3 gap-4 text-center py-4">
              <div>
                <Shield className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs font-medium">Verified Buyers</p>
              </div>
              <div>
                <TrendingUp className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs font-medium">10x More Reach</p>
              </div>
              <div>
                <Phone className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs font-medium">Expert Support</p>
              </div>
            </div>
          </div>

          {/* RIGHT: Embedded Form */}
          <div className="lg:col-span-7 order-1 lg:order-2 lg:sticky lg:top-4" data-testid="sell-form-column">
            <ListingFormEmbed listingType="sale" accentColor="emerald" returnPath="/sell/individual" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to Sell Your Property?</h2>
          <p className="text-slate-400 mb-6 text-sm">Join thousands of sellers who found buyers on ZenStudios</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" variant="outline" className="rounded-full border-slate-600 text-white hover:bg-white/10 px-8 h-11"
              onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20want%20to%20sell%20my%20property.', '_blank')}>
              <Phone className="w-4 h-4 mr-2" /> Talk to Expert
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellIndividual;
